import {
	ActionIcon,
	Box,
	createStyles,
	Group,
	Stack,
	Tooltip,
} from '@mantine/core';
import { Text } from '@repo/foundations';
import { useCallback } from 'react';
import { useIntegration, useUpdateMetric } from '../../../../../../api';
import { useGetOrCreateMetric } from '../../../../../../api/hooks/metricTerm/useGetOrCreateMetric';
import { entityIdFromCurrentPath } from '../../../../../../utils/shared.utils';
import { IntegrationSelector } from '../../../../../EntityModal/Metadata/IntegrationSelector';
import IntegrationLogo from '../../../../../IntegrationLogo';
import { QueryBlockTitle } from './QueryBlockTitle';

const useStyles = createStyles((theme) => ({
	wrapper: {
		background: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.radius.md,
		border: `solid ${theme.other.borderWidth.sm}px ${theme.other.getColor('border/secondary/default')}`,
	},
	code: {
		...theme.fn.fontStyles(),
		fontFamily: theme.fontFamilyMonospace,
		fontSize: theme.fontSizes.xs,
	},
}));

export function QueryBlockPlaceholder({
	id,
	integrationId,
	readOnly,
}: {
	id: string;
	integrationId?: string;
	readOnly: boolean;
}) {
	const { classes } = useStyles();

	const { data: metric } = useGetOrCreateMetric({
		id,
		integration: !!integrationId ? integrationId : undefined,
		hidden: true,
		parent: entityIdFromCurrentPath(),
	});

	const { mutateAsync } = useUpdateMetric({});

	const { data: integration } = useIntegration({
		id: metric.integration,
		options: {
			enabled: !!metric.integration,
		},
	});

	const handleChangeIntegration = useCallback(
		(newIntegrationId: string) => {
			mutateAsync({ data: { id, integration: newIntegrationId } });
		},
		[mutateAsync, id]
	);

	return (
		<Stack className={classes.wrapper} spacing="sm" pb="md">
			<Group spacing="sm" p="sm" noWrap>
				<Box style={{ flex: 1, position: 'relative' }}>
					<QueryBlockTitle metric={metric} readOnly={readOnly} />
				</Box>
				<Group spacing="xs" noWrap>
					<Group spacing="3xs" noWrap>
						{readOnly ? (
							<Box p="4xs">
								<IntegrationLogo
									integrationType={integration?.type}
									size={16}
								/>
							</Box>
						) : (
							<IntegrationSelector
								withinPortal={false}
								initialValue={metric.integration}
								onChange={handleChangeIntegration}
								target={
									<Tooltip label="Select integration">
										<ActionIcon>
											<IntegrationLogo
												integrationType={integration?.type}
												size={16}
											/>
										</ActionIcon>
									</Tooltip>
								}
								withOnlyQueryableIntegrations
							/>
						)}
					</Group>
				</Group>
			</Group>
			<Text
				size="sm"
				color="text/secondary/default"
				className={classes.code}
				pl="2xl"
			>
				Query will be editable when this template is used
			</Text>
		</Stack>
	);
}
